import * as React from "react";
import { useLocation } from "react-router-dom";
import { useMe } from "../hooks/useMe.js";
import {
  ensureAnonymousSession,
  identifyUser,
  markKidAnonSession,
  resetUser,
} from "../lib/analytics.js";

/**
 * Headless bridge that keeps PostHog's identity in sync with auth state.
 *
 * Reads the shared `['me']` query (no extra request — it's the same cache the
 * Nav, AuthGate, etc. already consume) and:
 *   - calls `identify` when a user is present / changes,
 *   - calls `reset` on the signed-in → signed-out transition,
 *   - keeps classroom students (COPPA) strictly anonymous.
 *
 * Centralizing this here means individual routes (Verify, Signup, AuthMenu)
 * don't each have to remember to identify/reset.
 *
 * The kid path: JoinRoute invalidates `['me']` the moment a kid joins or logs
 * in, so `me.classroom_student` appears here and we (a) sever any identified
 * adult from a previous session on this device (`ensureAnonymousSession` is
 * gated on PostHog's own persisted identity, NOT a per-page-load ref — a
 * teacher identified yesterday must still be cut loose today) and (b) mark the
 * anonymous id as kid-carrying so a later adult identify() on this device
 * never merges the kid's history into their person. The join surfaces are
 * treated as presumptively-kid BEFORE any login so the whole student funnel
 * runs anonymous from its first event.
 */
export function AnalyticsIdentity(): null {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const userId = me?.user?.id ?? null;
  const wasSignedIn = React.useRef(false);

  const isKid = Boolean(me?.classroom_student);
  const onJoinSurface = pathname === "/join" || /^\/c\/./.test(pathname);

  React.useEffect(() => {
    if (isKid || onJoinSurface) {
      // Anonymous zone. ensureAnonymousSession no-ops when already anonymous,
      // so a kid reloading mid-homework keeps ONE anon id (funnels stay
      // stitched); an identified adult (teacher projector, parent laptop) is
      // reset to a fresh anon id before any kid event fires.
      ensureAnonymousSession();
      if (isKid) markKidAnonSession();
      wasSignedIn.current = false;
      return;
    }
    if (me?.user) {
      // A signed-in adult leaving the join surface re-identifies here (their
      // own demo events merge back to their person — correct attribution).
      identifyUser(me);
      wasSignedIn.current = true;
    } else if (wasSignedIn.current) {
      // Only reset on a real sign-out, not on the initial anonymous load.
      resetUser();
      wasSignedIn.current = false;
    }
    // `me` identity changes whenever the query refetches; gating on userId +
    // premium (+ the kid/join-surface flags) — deliberately NOT the whole `me`
    // object — keeps us from re-identifying on every background refresh.
  }, [userId, me?.premium, isKid, onJoinSurface]);

  return null;
}
